import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import './EmployeeVacancy.css';
import { selectStyles } from '../../../assets/Styles/selectStyle';
import { getDistrict, getStates } from '../../../services/monthlyAttendanceService';
import { capitalizeFirstLetter } from '../../../utils/validations';
import {
    getDesignationSummary,
    getDesignationSummaryDrillDown,
} from '../../../services/employeePostingService';

const EmployeesAsRequirement = () => {
    const [state, setState] = useState(3);
    const [district, setDistrict] = useState(null);

    const [stateOptions, setStateOptions] = useState([]);
    const [districtOptions, setDistrictOptions] = useState([]);
    const [summaryData, setSummaryData] = useState([]);
    const [loading, setLoading] = useState(false);

    // Drilldown state
    const [view, setView] = useState('summary'); // 'summary' | 'drilldown'
    const [drillData, setDrillData] = useState([]);
    const [drillLoading, setDrillLoading] = useState(false);
    const [selectedDesignation, setSelectedDesignation] = useState(null);

    const effectRan = useRef(false);

    const [isFocused, setIsFocused] = useState({ state: false, district: false, });
    const [errors, setErrors] = useState({});

    // Fetch states
    useEffect(() => {
        const fetchStates = async () => {
            try {
                const data = await getStates();
                setStateOptions(
                    data.map(item => ({
                        value: item.id,
                        label: capitalizeFirstLetter(item.stateName),
                    }))
                );
            } catch (error) {
                console.error('Error fetching states:', error);
                setErrors(prev => ({ ...prev, general: 'Failed to load states' }));
            }
        };
        if (!effectRan.current) {
            fetchStates();
        }
        return () => {
            effectRan.current = true;
        };
    }, []);

    // Fetch districts when state changes
    useEffect(() => {
        if (state) {
            const fetchDistricts = async () => {
                try {
                    const data = await getDistrict(state);
                    setDistrictOptions(
                        data.map(item => ({
                            value: item.districtId,
                            label: item.districtName,
                        }))
                    );
                } catch (err) {
                    console.error('Error fetching districts:', err);
                    setErrors(prev => ({ ...prev, general: 'Failed to load districts' }));
                }
            };
            fetchDistricts();
        } else {
            setDistrictOptions([]);
            setDistrict(null);
        }
    }, [state]);

    const handleInputChange = (selectedOption, field) => {
        const value = selectedOption ? selectedOption.value : null;
        switch (field) {
            case 'state':
                setState(value);
                setDistrict(null);
                setSummaryData([]);
                resetDrill();
                break;
            case 'district':
                setDistrict(value);
                resetDrill();
                break;
            default:
                break;
        }
        setErrors(prev => ({ ...prev, [field]: null }));
    };

    const resetDrill = () => {
        setView('summary');
        setDrillData([]);
        setSelectedDesignation(null);
    };

    // Fetch designation summary
    const handleGetDetails = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrors(prev => ({ ...prev, general: null }));
        resetDrill();

        try {
            const data = await getDesignationSummary(state, district);
            setSummaryData(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Error fetching designation summary:', err);
            setErrors(prev => ({
                ...prev,
                general: 'Failed to fetch designation summary',
            }));
            setSummaryData([]);
        } finally {
            setLoading(false);
        }
    };

    // Drilldown — open new table
    const handleRowClick = async (designationGroup) => {
        setSelectedDesignation(designationGroup);
        setView('drilldown');
        setDrillLoading(true);
        setDrillData([]);

        try {
            const data = await getDesignationSummaryDrillDown(
                state,
                designationGroup,
                district
            );
            setDrillData(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Error fetching drilldown:', err);
            setDrillData([]);
        } finally {
            setDrillLoading(false);
        }
    };

    // Back to summary table
    const handleBack = () => {
        resetDrill();
    };

    // Clear filters
    const handleClear = () => {
        setDistrict(null);
        setSummaryData([]);
        resetDrill();
        setErrors({});
    };

    return (
        <div className="evp-container">
            {/* ===== Filter Box (always visible) ===== */}
            <div className="evp-box">
                <h2 className="evp-heading">Employees As Per Contract</h2>
                {errors.general && <div className="evp-error-msg-static">{errors.general}</div>}

                <form onSubmit={handleGetDetails}>
                    <div className="evp-form-row">
                        <div className="evp-field">
                            <div className={`evp-float-wrap ${isFocused.state || state ? 'evp-float-active' : ''}`}>
                                <Select
                                    name="state"
                                    options={stateOptions}
                                    value={stateOptions.find(opt => opt.value === state) || null}
                                    onChange={option => handleInputChange(option, 'state')}
                                    onFocus={() => setIsFocused(prev => ({ ...prev, state: true }))}
                                    onBlur={() => setIsFocused(prev => ({ ...prev, state: false }))}
                                    placeholder=" "
                                    isClearable
                                    menuPortalTarget={document.body}
                                    styles={selectStyles}
                                />
                                <label>
                                    <span className="evp-required">*</span>State
                                </label>
                            </div>
                        </div>

                        <div className="evp-field">
                            <div className={`evp-float-wrap ${isFocused.district || district ? 'evp-float-active' : ''}`}>
                                <Select
                                    name="district"
                                    options={districtOptions}
                                    value={districtOptions.find(opt => opt.value === district) || null}
                                    onChange={option => handleInputChange(option, 'district')}
                                    onFocus={() => setIsFocused(prev => ({ ...prev, district: true }))}
                                    onBlur={() => setIsFocused(prev => ({ ...prev, district: false }))}
                                    placeholder=" "
                                    isClearable
                                    menuPortalTarget={document.body}
                                    styles={selectStyles}
                                />
                                <label>District</label>
                            </div>
                        </div>
                    </div>

                    <div className="evp-btn-row">
                        <button type="button" className="evp-btn evp-btn-clear" onClick={handleClear}>
                            Clear
                        </button>
                        <button
                            type="submit"
                            className="evp-btn evp-btn-primary"
                            disabled={loading}
                        >
                            {loading ? 'Loading...' : 'Get Details'}
                        </button>
                    </div>
                </form>
            </div>

            {/* ===== SUMMARY VIEW ===== */}
            {view === 'summary' && (
                <div className="evp-table-wrap">
                    <table className="evp-table">
                        <thead>
                            <tr>
                                <th>S.No</th>
                                <th>Designation</th>
                                <th>Total Required</th>
                                <th>Total Deployed</th>
                                {/* <th>Vacancy</th> */}
                            </tr>
                        </thead>
                        <tbody>
                            {summaryData.length > 0 ? (
                                summaryData.map((item, index) => {
                                    const vacancy = (item.totalRequired || 0) - (item.totalDeployed || 0);
                                    return (
                                        <tr
                                            key={item.designationGroup || index}
                                            className="evp-row-clickable"
                                            onClick={() => handleRowClick(item.designationGroup)}
                                        >
                                            <td>{index + 1}</td>
                                            <td>{item.designationGroup || 'N/A'}</td>
                                            <td>{item.totalRequired ?? 0}</td>
                                            <td>{item.totalDeployed ?? 0}</td>
                                            {/* <td>{vacancy}</td> */}
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan="5" className="evp-no-data">
                                        {loading ? 'Loading...' : 'No data found.'}
                                    </td>
                                </tr>
                            )}
                        </tbody>

                        {/* ===== TOTAL ROW ===== */}
                        {summaryData.length > 0 && (
                            <tfoot>
                                <tr className="evp-total-row bg-warning-subtle">
                                    <td colSpan="2" className="evp-total-label">Total</td>
                                    <td>
                                        {summaryData.reduce((sum, item) => sum + (item.totalRequired || 0), 0)}
                                    </td>
                                    <td>
                                        {summaryData.reduce((sum, item) => sum + (item.totalDeployed || 0), 0)}
                                    </td>
                                    {/* <td>
                                        {summaryData.reduce(
                                            (sum, item) =>
                                                sum +
                                                ((item.totalRequired || 0) - (item.totalDeployed || 0)),
                                            0
                                        )}
                                    </td> */}
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            )}

            {/* ===== DRILLDOWN VIEW ===== */}
            {view === 'drilldown' && (
                <div className="evp-table-wrap">
                    <div className="evp-drill-header">
                        <h3 className="evp-drill-title">
                            Location-wise Details — {selectedDesignation}
                        </h3>
                        <button className="evp-back-btn" onClick={handleBack}>
                            ← Back
                        </button>
                    </div>

                    {drillLoading ? (
                        <div className="evp-drill-loading">Loading details...</div>
                    ) : drillData.length > 0 ? (
                        <table className="evp-table evp-drill-table">
                            <thead>
                                <tr>
                                    <th>S.No</th>
                                    <th>Location</th>
                                    <th>Designation</th>
                                    <th>Total Required</th>
                                    <th>Total Deployed</th>
                                    {/* <th>Vacancy</th> */}
                                </tr>
                            </thead>
                            <tbody>
                                {drillData.map((row, idx) => {
                                    const vacancy =
                                        (row.requiredCount || 0) - (row.deployedCount || 0);
                                    return (
                                        <tr key={idx}>
                                            <td>{idx + 1}</td>
                                            <td>{row.locationName || row.districtName || row.location || 'N/A'}</td>
                                            <td>{row.designation || row.designationGroup || selectedDesignation}</td>
                                            <td>{row.requiredCount ?? 0}</td>
                                            <td>{row.deployedCount ?? 0}</td>
                                            {/* <td>{vacancy}</td> */}
                                        </tr>
                                    );
                                })}
                            </tbody>

                            {/* ===== TOTAL ROW ===== */}
                            <tfoot>
                                <tr className="evp-total-row bg-warning-subtle">
                                    <td colSpan="3" className="evp-total-label">Total</td>
                                    <td>
                                        {drillData.reduce((sum, row) => sum + (row.requiredCount || 0), 0)}
                                    </td>
                                    <td>
                                        {drillData.reduce((sum, row) => sum + (row.deployedCount || 0), 0)}
                                    </td>
                                    {/* <td>
                                        {drillData.reduce(
                                            (sum, row) =>
                                                sum +
                                                ((row.requiredCount || 0) - (row.deployedCount || 0)),
                                            0
                                        )}
                                    </td> */}
                                </tr>
                            </tfoot>
                        </table>
                    ) : (
                        <div className="evp-drill-loading">No location details found.</div>
                    )}
                </div>
            )}
        </div>
    );
};

export default EmployeesAsRequirement;

